import { CommonModule } from '@angular/common';
import { TranslateService } from '@ngx-translate/core';
import {
  AfterViewInit,
  Component,
  computed,
  HostBinding,
  inject,
  model,
  OnDestroy,
  signal,
  ViewChild,
  viewChild,
  ElementRef,
} from '@angular/core';
import { MatCardModule } from '@angular/material/card';
import { TPipe, TRANSLATION_PREFIX } from '@pipes/t.pipe';
import { AbbCommonModule } from 'app/abb-common.module';
import { Router, RouterModule } from '@angular/router';
import { FormGroup, FormsModule } from '@angular/forms';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { AbbCardComponent } from '@components/abb-card/abb-card.component';
import { BehaviorSubject, firstValueFrom, of, Subscription, timer } from 'rxjs';
import {
  map,
  switchMap,
  catchError,
  filter,
  delay,
  retry,
  first,
} from 'rxjs/operators';
import { ToastService } from '@services/ui/toast.service';
import { WizardCardStepperComponent } from './wizard-card-stepper.component';
import { PropertyFormComponent } from '@pages/properties/property-form/property-form.component';
import { Apollo } from 'apollo-angular';
import { createPropertyMutation, propertyListQuery } from '@graphql/property';
import {
  CreatePropertyMutation,
  CreatePropertyMutationVariables,
  Device,
  FirmwareVersionQuery,
  FirmwareVersionQueryVariables,
  FoundDevicesListQuery,
  FoundDevicesListQueryVariables,
  GatewayConnectionCheckTokenQuery,
  GatewayConnectionCheckTokenQueryVariables,
  GatewayConnectionTokenQuery,
  GatewayConnectionTokenQueryVariables,
  GatewayToken,
  Property,
  RequestFirmwareUpdateMutation,
  RequestFirmwareUpdateMutationVariables,
  SaveFirstAccessWizardStepperStatusMutation,
  SaveFirstAccessWizardStepperStatusMutationVariables,
  Status,
  StepDetailsInput,
  User,
} from 'gql/graphql';
import { DevicesConfigurationComponent } from './devices-configuration/devices-configuration.component';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import {
  checkSaveFirstAccessWizard,
  firmwareVersion,
  foundDevicesListQuery,
  gatewayConnectionCheckTokenQuery,
  gatewayConnectionTokenQuery,
  requestFirmwareUpdateMutation,
  saveStepperStatusMutation,
} from '@graphql/commissioning';
import { NumberPipe } from '@pipes/number.pipe';
import { URLS } from '@constants/urls';
import { OverlayService } from '@services/ui/overlay.service';
import { CognitoService } from '@services/cognito/cognito.service';
import { getErrorMessage } from '@utils/errors';
import { t } from '@utils/strings';
import { HeaderComponent } from '@layout/header.component';

export const FIRST_ACCESS_WIZARD_LAST_STEP = 4;

type PartialProperty =
  | (Omit<
      Property,
      | 'status'
      | 'todaysConsumption'
      | 'thisMonthsConsumption'
      | 'actualCosts'
      | 'consumption'
      | 'contacts'
    > & { contacts: Omit<User, 'imgUrl' | 'phone'>[] })
  | null;

type PartialDevice = Omit<Device, 'groups' | 'consumption'> | null;

interface CheckSaveFirstAccessWizardResponse {
  isFromProperty: boolean;
  proceedToGateway: boolean;
  isFirstUser: boolean;
}

@Component({
  selector: 'abb-first-access-wizard',
  standalone: true,
  imports: [
    RouterModule,
    CommonModule,
    MatCardModule,
    TPipe,
    NumberPipe,
    AbbCommonModule,
    FormsModule,
    MatCheckboxModule,
    AbbCardComponent,
    WizardCardStepperComponent,
    PropertyFormComponent,
    DevicesConfigurationComponent,
    MatProgressSpinnerModule,
    HeaderComponent,
  ],
  template: `
    <abb-header [isWizardHeader]="true" />
    <div class="relative block overflow-y-auto w-full h-[calc(100vh-4rem)]">
      <!-- Video background -->
      <video
        #backgroundVideo
        class="absolute top-0 left-0 w-full h-full object-cover -z-10"
        autoplay
        muted
        loop
        playsinline
        preload="auto"
        (loadeddata)="onVideoLoaded()"
        (canplay)="onVideoCanPlay()"
      >
        <source
          src="https://new.abb.com/videos/librariesprovider84/smart-buildings/insite-energy-pro-loop2.mp4?sfvrsn=99f7670d_4"
          type="video/mp4"
        />
        <!-- Fallback per browser che non supportano il video -->
        <div
          class="absolute top-0 left-0 w-full h-full bg-center bg-cover bg-no-repeat"
          style="background-image: url(/assets/images/abb-bg.png);"
        ></div>
      </video>
      @if(showDisclaimer()){
      <abb-card
        [cardTitle]="'Free trial period' | t"
        [cardHeight]="'h-[600px]'"
      >
        <ng-container card-content>
          <div>
            <p class="mb-2">{{ 'earlyUserBenefitsMessagePart1' | t }}</p>
            <p>{{ 'earlyUserBenefitsMessagePart2' | t }}</p>
            <ul
              class="mb-2"
              style="list-style-type: disc; padding-left: 1.5rem;"
            >
              <li>{{ 'earlyUserBenefitsMessageItem1' | t }}</li>
              <li>{{ 'earlyUserBenefitsMessageItem2' | t }}</li>
            </ul>
            <p class="mb-2">{{ 'earlyUserBenefitsMessagePart3' | t }}</p>
          </div>

          <div
            class="flex-1 inline-flex flex-col justify-center min-w-0 text-center"
          >
            <mat-checkbox
              [(ngModel)]="acceptTerms"
              id="acceptTerms"
              name="acceptTerms"
              color="primary"
              class="border-font-checkbox no-hover-checkbox rounded-checkbox"
            >
              <a
                (click)="downloadTermsDocument($event)"
                class="text-abb-primary hover:underline font-semibold"
                >{{ 'Accept terms & conditions' | t }}</a
              >
            </mat-checkbox>
          </div>

          <p
            class="text-xs text-abb-font/60 text-center mt-2"
            [innerHTML]="'privacy notice' | t"
          ></p>
        </ng-container>
        <ng-container card-actions>
          <abb-button
            class="w-32"
            (click)="goNext()"
            [disabled]="!acceptTerms() || nextStepBtnLoading()"
            [loading]="nextStepBtnLoading()"
            >{{ 'Next' | t }}</abb-button
          >
        </ng-container>
      </abb-card>
      } @else {
      <abb-wizard-card-stepper
        #stepperRef
        [steps]="steps()"
        [onBeforeGoNextStep]="onBeforeGoNextStep"
        (changeStep)="onChangeStep($event)"
        [showBackBtn]="showBackBtn()"
        [onGoBack]="goBack"
        [hideNextStepBtn]="hideNextStepBtn()"
        [nextStepBtnDisabled]="nextStepBtnDisabled()"
        [nextStepBtnLoading]="nextStepBtnLoading()"
      >
        <ng-template #stepContent let-index="index">
          @if(index === 0 && (index === step())) {
          <abb-property-form
            #propertyFormRef
            class="flex-1"
            [stepperView]="false"
            [mode]="propertyFormEditing() ? 'edit' : 'new'"
            [initValues]="propertyFormInitValues()"
            (formValidationStatusChange)="handleSubmitStatusBtn($event)"
            (formSubmit)="onSubmit($event)"
          ></abb-property-form>
          } @else if(index === 1) {
          <div
            class="flex flex-col flex-1 min-h-[232px] items-center justify-center"
          >
            @if(!checkingGatewayConnession() && notLinked()) {
            <div class="flex flex-col gap-14">
              <div class="flex flex-col">
                <span class="text-abb-primary font-semibold">{{
                  'Token Generation' | t
                }}</span>
                <span class="max-w-96">{{
                  'Insert the following token in the SCU200 webserver for starting the linking process'
                    | t
                }}</span>
              </div>
              <div class="flex-1 inline-flex justify-center items-center">
                <span class="text-3xl font-semibold">{{
                  gatewayToken()?.token
                }}</span>
              </div>
              @if(waitingForWebServerResponse()) {
              <div class="flex flex-col gap-1">
                @for(attempt of linkAttempts(); track attempt) {
                <span class="flex flex-row gap-4">
                  @if(attempt.loading) {
                  <mat-progress-spinner
                    class="button-spinner"
                    [diameter]="20"
                    [mode]="'indeterminate'"
                  ></mat-progress-spinner>
                  } @else {
                  <abb-icon
                    [name]="'mat/error_outline'"
                    [ngClass]="'text-abb-error'"
                  ></abb-icon>
                  }
                  {{ attempt.message }}...
                </span>
                }
              </div>
              }
            </div>
            } @else { @if(gateway(); as gateway) {
            <div class="flex flex-row gap-3">
              <div class="w-[90px]">
                <img
                  src="/assets/images/SCU200.png"
                  alt="SCU200"
                  class="object-cover w-auto h-[180px]"
                />
              </div>
              <div
                class="flex-1 flex flex-col gap-6 justify-start mt-6 min-h-52 min-w-[347px]"
              >
                <span class="w-full inline-flex justify-between">
                  {{
                    '{name} Linked Successfully'
                      | t
                        : {
                            name: gateway?.name
                          }
                  }}<abb-icon
                    name="mat/task_alt"
                    class="text-abb-success"
                  ></abb-icon
                ></span>

                <span class="font-bold flex items-center gap-2">
                  {{ 'Fw version' | t }}: @if (firmwareVersion(); as version) {
                  <span>{{ version }}</span>
                  } @else {
                  <mat-progress-spinner
                    [diameter]="16"
                    [mode]="'indeterminate'"
                    class="mr-1"
                    [ngStyle]="{
                      '--mdc-circular-progress-active-indicator-color':
                        'rgb(var(--abb-color-black)/0.2)'
                    }"
                  ></mat-progress-spinner>
                  }
                </span>

                @if(isLastFirmwareVersion() && firmwareVersion()) {
                <span class="w-full inline-flex justify-between">
                  {{ 'Latest firmware version updated' | t }}
                  <abb-icon
                    name="mat/task_alt"
                    class="text-abb-success"
                  ></abb-icon>
                </span>
                }
              </div>
            </div>
            } @else if (checkingGatewayConnession()) {
            <mat-spinner class="mx-auto"></mat-spinner>
            }}
          </div>
          } @else if(index === 2) {
          <div class="flex flex-col h-[450px] !my-4 min-w-[481px]">
            @if (loadingDevices | async) {
            <div class="flex items-center justify-center h-full w-full">
              <mat-spinner class="mx-auto" [diameter]="50"></mat-spinner>
            </div>
            } @else if (noDevices()) {
            <div class="flex items-center justify-center h-full">
              <span class="text-center">No data</span>
            </div>
            }@else{
            <abb-devices-configuration
              [devices]="foundDevicesList()"
              [scanAgainDisabled]="loadingDevices.value"
              [scanAgainLoading]="loadingDevices.value"
              (scanAgain)="scanAgain()"
            />
            }
          </div>
          } @else if(index === 3){
          <div class="flex flex-col gap-3">
            <div class="flex flex-col">
              @if (property$ | async; as property) {
              <span class="font-semibold">{{
                'Property {name}' | t : { name: property.name }
              }}</span>
              <span>{{ property.fullAddress }}</span>
              <span>
                {{
                  property.coordinates.lat
                    | number : { maximumFractionDigits: 4 }
                }}°N
                {{
                  property.coordinates.lng
                    | number : { maximumFractionDigits: 4 }
                }}°E
              </span>
              }
            </div>
            <div class="flex flex-col">
              <span class="font-semibold">{{
                '{name} connected' | t : { name: gateway()?.name }
              }}</span>
              <span>{{
                '{count} devices connected'
                  | t : { count: connectedDevicesCount() }
              }}</span>
            </div>
            <div class="flex flex-col h-full">
              <span class="font-semibold">{{ 'Devices Configured' | t }}</span>
              @if (noDevices()) {
              <span>{{ 'No devices available' | t }}</span>
              } @else {
              <div class="flex-grow overflow-y-auto ">
                <!-- Layout a colonne multiple per ottimizzare lo spazio --- prima una colonna con max-h-40 -->
                <ul
                  class="list-disc pl-5 columns-2 md:columns-3 lg:columns-4 gap-4 break-inside-avoid"
                >
                  @for (group of devices() | keyvalue; track group) { @for
                  (device of group.value.devices; track device?.id) {
                  <li class="break-inside-avoid mb-1">{{ device?.name }}</li>
                  } }
                </ul>
              </div>
              }
            </div>
          </div>
          }
        </ng-template>
        <ng-template #lastStepBtn>
          <div class="flex flex-col gap-5 w-full">
            <!-- Riga 1: Opzioni aggiuntive -->
            <div class="flex flex-row gap-2 w-full">
              <abb-button
                variant="secondary"
                class="min-w-max h-12 text-sm whitespace-nowrap justify-center"
                [className]="'!rounded-xl'"
                (onclick)="configureAnotherGateway()"
                [loading]="configureAnotherGatewayBtnLoading()"
                [disabled]="
                  configureAnotherGatewayBtnLoading() ||
                  configureAnotherPropertyBtnLoading() ||
                  goToDashboardBtnLoading()
                "
              >
                {{ 'Configure another gateway for this property' | t }}
              </abb-button>

              <abb-button
                variant="secondary"
                class="min-w-max h-12 text-sm whitespace-nowrap justify-center"
                [className]="'!rounded-xl'"
                (onclick)="configureAnotherProperty()"
                [loading]="configureAnotherPropertyBtnLoading()"
                [disabled]="
                  configureAnotherGatewayBtnLoading() ||
                  configureAnotherPropertyBtnLoading() ||
                  goToDashboardBtnLoading()
                "
              >
                {{ 'Configure another property' | t }}
              </abb-button>
            </div>

            <!-- Testo informativo -->
            <span class="italic text-wrap max-w-[600px]">
              {{
                'You will have the chance to configure additional gateways or new properties also later on directly from the Dashboard'
                  | t
              }}
            </span>

            <!-- Riga 2: Navigazione -->
            <div class="flex flex-row justify-between items-center w-full">
              <abb-button
                variant="clean"
                class="min-w-max h-12 text-sm whitespace-nowrap justify-center"
                [className]="'!rounded-xl'"
                (onclick)="goBackBtn()"
                [loading]="configureAnotherGatewayBtnLoading()"
                [disabled]="
                  configureAnotherGatewayBtnLoading() ||
                  configureAnotherPropertyBtnLoading() ||
                  goToDashboardBtnLoading()
                "
              >
                {{ 'back' | t }}
              </abb-button>

              <abb-button
                class="min-w-max h-12 text-sm whitespace-nowrap justify-center"
                [className]="'!rounded-xl'"
                (onclick)="goToDashboard()"
                [loading]="goToDashboardBtnLoading()"
                [disabled]="
                  configureAnotherGatewayBtnLoading() ||
                  configureAnotherPropertyBtnLoading() ||
                  goToDashboardBtnLoading()
                "
              >
                {{ 'All settled - Go to dashboard' | t }}
              </abb-button>
            </div>
          </div>
        </ng-template>
      </abb-wizard-card-stepper>
      }
    </div>
  `,
  styles: `
    .bg-white-field {
      --mdc-filled-text-field-container-color: var(--abb-color-white);
      --mat-form-field-state-layer-color: transparent;
    }
    .scroll-container::before,
    .scroll-container::after {
      content: '';
      position: absolute;
      left: 0;
      right: 0;
      height: 20px;
      pointer-events: none;
      z-index: 1;
    }
    .scroll-container::before {
      top: 0;
      background: linear-gradient(to bottom, rgba(0, 0, 0, 0.2), rgba(0, 0, 0, 0));
    }
    .scroll-container::after {
      bottom: 0;
      background: linear-gradient(to top, rgba(0, 0, 0, 0.2), rgba(0, 0, 0, 0));
    }
    `,
  providers: [
    {
      provide: TRANSLATION_PREFIX,
      useValue: 'wizard_first_access',
    },
  ],
  host: { class: 'block w-full h-full flex-1' },
})
export class WizardFirstAccessComponent implements OnDestroy, AfterViewInit {
  @HostBinding('attr.id') componentid = 'wizard-component';
  // @ViewChild(WizardCardStepperComponent)
  @ViewChild('backgroundVideo', { static: false })
  backgroundVideo!: ElementRef<HTMLVideoElement>;

  private firmwareIntervalId: any = null;
  private devicesPollingIntervalId: any = null;
  private firmwareVersionPollingId: any = null;
  wizardCard!: WizardCardStepperComponent;
  stepperRef = viewChild<WizardCardStepperComponent>('stepperRef');
  propertyFormRef = viewChild<PropertyFormComponent>('propertyFormRef');
  prefix = inject(TRANSLATION_PREFIX);
  acceptTerms = model(false);
  showDisclaimer = signal(true);
  checkingGatewayConnession = signal(false);
  notLinked = signal(false);
  waitingForWebServerResponse = signal(false);
  linkAttempts = signal<Array<{ loading: boolean; message: string }>>([]);
  nextStepBtnDisabled = signal(false);
  nextStepBtnLoading = signal(false);
  configureAnotherGatewayBtnLoading = signal(false);
  configureAnotherPropertyBtnLoading = signal(false);
  goToDashboardBtnLoading = signal(false);
  showBackBtn = computed(() => !!this.propertyFormRef()?.showContacts());
  hideNextStepBtn = computed(
    () =>
      this.currentStep() === 1 &&
      ((!this.checkingGatewayConnession() &&
        this.notLinked()) /* && this.waitingForWebServerResponse() */ ||
        (this.fwUpdateAvailable() && !this.fwUpdateProcessed()))
  );
  steps = signal([
    { title: 'Add New Property' },
    { title: 'Gateway Connection', subtitle: 'SCU200 Link' },
    { title: 'Devices Configuration' },
    { title: 'Final Summary' },
  ]);
  gatewayToken = signal<GatewayToken | null>(null);

  fwUpdateAvailable = computed(
    () => !!this.gateway()?.firmware?.updateAvailable
  );
  fwIsUpToDate = computed(() => {
    const gateway = this.gateway();
    const isLast = this.isLastFirmwareVersion();
    return !!(gateway?.firmware && !gateway.firmware.updateAvailable && isLast);
  });
  fwUpdateProcessed = signal(false);
  updatingFw = signal(false);
  fwUpdated = signal(false);
  currentStep = signal(0);
  propertyFormEditing = signal(false);
  readonly propertyFormInitValues = computed(() => {
    return this.propertyFormEditing()
      ? {
          id: this.propertySubject.value?.id ?? '',
          name: this.propertySubject.value?.name ?? '',
          address: this.propertySubject.value?.address ?? '',
          fullAddress: this.propertySubject.value?.fullAddress ?? '',
          country: this.propertySubject.value?.country ?? '',
          city: this.propertySubject.value?.city ?? '',
          postalCode: this.propertySubject.value?.postalCode ?? '',
          timezone: this.propertySubject.value?.timezone ?? '',
          currency: this.propertySubject.value?.currency ?? '',
          imgUrl: this.propertySubject.value?.imgUrl ?? '',
          coordinates: this.propertySubject.value?.coordinates ?? {
            lat: 0,
            lng: 0,
          },
          contacts: this.propertySubject.value?.contacts ?? [],
        }
      : undefined;
  });
  private propertySubject = new BehaviorSubject<PartialProperty>(null);
  property$ = this.propertySubject.asObservable();
  gateway = signal<
    GatewayConnectionCheckTokenQuery['gatewayConnectionCheckToken'] | null
  >(null);
  firmwareVersionData = signal<{
    gatewayId: string;
    firmwareVersion: string;
    IsLastFirmwareVersion: boolean;
  } | null>(null);

  firmwareVersion = computed(
    () => this.firmwareVersionData()?.firmwareVersion || null
  );

  isLastFirmwareVersion = computed(
    () => this.firmwareVersionData()?.IsLastFirmwareVersion || false
  );
  connectedDevicesCount = computed(() => {
    return (
      this.foundDevicesList()?.filter((d) => d?.status === Status.Ok)?.length ??
      0
    );
  });
  isFirstUser = signal<boolean>(false);
  noDevices = computed(() => {
    return this.foundDevicesList()?.length === 0;
  });
  foundDevicesList = signal<FoundDevicesListQuery['foundDevicesList'] | null>(
    null
  );
  loadingDevices = new BehaviorSubject<boolean>(false);
  devices = signal<Record<
    string,
    {
      communicationProtocol: string;
      devices: FoundDevicesListQuery['foundDevicesList'];
    }
  > | null>(null);
  step = computed(() => this.cognitoService.wizardStep().step);
  stepDetails = computed(() => this.cognitoService.wizardStep().stepDetails);

  private subscriptions: Subscription[] = [];
  constructor(
    private readonly apollo: Apollo,
    private readonly toast: ToastService,
    private readonly overlay: OverlayService,
    private readonly router: Router,
    private readonly translate: TranslateService,
    private readonly cognitoService: CognitoService
  ) {
    switch (this.step()) {
      case 0:
        this.acceptTerms.set(this.stepDetails()?.termsAndConditions!);
        if (this.stepDetails()?.termsAndConditions) {
          this.nextStepBtnDisabled.set(true);
          this.showDisclaimer.set(false);
        }
        break;
      case 1:
        this.showDisclaimer.set(false);
        this.acceptTerms.set(this.stepDetails()?.termsAndConditions!);
        this.propertyFormEditing.set(true);
        this.propertySubject.next(
          (this.stepDetails()?.property as PartialProperty) ?? null
        );
        break;
      case 2:
        this.showDisclaimer.set(false);
        this.acceptTerms.set(this.stepDetails()?.termsAndConditions!);
        this.propertyFormEditing.set(true);
        this.propertySubject.next(
          (this.stepDetails()?.property as PartialProperty) ?? null
        );
        this.gateway.set(this.stepDetails()?.gateway!);
        this.notLinked.set(!!this.stepDetails()?.gateway);
        // this.fwUpdateProcessed.set(
        //   this.fwUpdateAvailable() &&
        //     !this.stepDetails()?.gateway?.firmware?.updateAvailable
        // );
        // this.fwUpdated.set(
        //   this.fwUpdateAvailable() &&
        //     !this.stepDetails()?.gateway?.firmware?.updateAvailable
        // );
        break;
      case 3:
      case 4:
        this.showDisclaimer.set(false);
        this.acceptTerms.set(this.stepDetails()?.termsAndConditions!);
        this.propertyFormEditing.set(true);
        this.propertySubject.next(
          (this.stepDetails()?.property as PartialProperty) ?? null
        );
        this.gateway.set(this.stepDetails()?.gateway!);
        this.notLinked.set(!!this.stepDetails()?.gateway);
        // this.fwUpdateProcessed.set(
        //   this.fwUpdateAvailable() &&
        //     !this.stepDetails()?.gateway?.firmware?.updateAvailable
        // );
        // this.fwUpdated.set(
        //   this.fwUpdateAvailable() &&
        //     !this.stepDetails()?.gateway?.firmware?.updateAvailable
        // );
        const devices = (this.stepDetails()?.devices as PartialDevice[]) ?? [];
        this.foundDevicesList.set(devices);
        this.setGroupedDevices(devices);
        break;
      default:
        break;
    }
  }

  ngAfterViewInit(): void {
    this.stepperRef()?.goToStep(this.step());
    // Gestione del video di background
    this.ensureVideoPlays();
  }

  onVideoLoaded(): void {
    // Il video è stato caricato, prova a farlo partire
    this.ensureVideoPlays();
  }

  onVideoCanPlay(): void {
    // Il video può essere riprodotto, prova a farlo partire
    this.ensureVideoPlays();
  }

  private ensureVideoPlays(): void {
    if (this.backgroundVideo?.nativeElement) {
      const video = this.backgroundVideo.nativeElement;
      video.muted = true; // Assicurati che sia muto per l'autoplay

      // Prova a far partire il video
      const playPromise = video.play();

      if (playPromise !== undefined) {
        playPromise
          .then(() => {
            // Video started successfully
            console.log('Background video started successfully');
          })
          .catch((error) => {
            console.warn('Background video autoplay was prevented:', error);
            // Fallback: prova a far partire il video su user interaction
            this.setupVideoInteractionFallback();
          });
      }
    }
  }

  private setupVideoInteractionFallback(): void {
    if (this.backgroundVideo?.nativeElement) {
      const video = this.backgroundVideo.nativeElement;

      // Handler per far partire il video al primo click/touch sulla pagina
      const startVideoOnInteraction = () => {
        video
          .play()
          .then(() => {
            console.log('Background video started after user interaction');
            // Rimuovi gli event listeners una volta che il video è partito
            document.removeEventListener('click', startVideoOnInteraction);
            document.removeEventListener('touchstart', startVideoOnInteraction);
          })
          .catch((error) => {
            console.warn(
              'Could not start background video even after user interaction:',
              error
            );
          });
      };

      // Aggiungi gli event listeners
      document.addEventListener('click', startVideoOnInteraction, {
        once: true,
      });
      document.addEventListener('touchstart', startVideoOnInteraction, {
        once: true,
      });
    }
  }

  postponeFwUpdate(): void {
    this.fwUpdateProcessed.set(true);
  }

  updateFw(): void {
    this.fwUpdateProcessed.set(true);
    this.updatingFw.set(true);
    this.nextStepBtnLoading.set(true);
    this.apollo
      .mutate<
        RequestFirmwareUpdateMutation,
        RequestFirmwareUpdateMutationVariables
      >({
        mutation: requestFirmwareUpdateMutation,
        variables: {
          devices: [
            {
              id: this.gateway()!.id,
              serialNumberSCU200: this.gateway()!.serialNumber,
            },
          ],
        },
      })
      .subscribe({
        next: ({ data }) => {
          // L'aggiornamento è stato richiesto con successo, ma non ancora completato
          this.updatingFw.set(false);
          this.nextStepBtnLoading.set(false);
          this.fwUpdateProcessed.set(true); // Il processo è stato avviato
          // Non impostiamo fwUpdated, sarà gestito dal computed signal quando la versione sarà effettivamente verificata

          this.gateway.update((prev) => {
            return prev
              ? {
                  ...prev,
                  firmware: { ...data!.requestFirmwareUpdate[0] },
                }
              : prev;
          });


        },
        error: () => {
          this.updatingFw.set(false);
          this.nextStepBtnLoading.set(false);
          // Non impostiamo più fwUpdated, è gestito dal computed signal
        },
      });
  }

  downloadTermsDocument(event: MouseEvent) {
    // Previene la navigazione standard
    event.preventDefault();
    event.stopPropagation();

    // Ottieni la lingua corrente
    const currentLang = this.translate.currentLang || 'en-EN';
    let documentPath = '';
    if (currentLang === 'it-IT') {
      documentPath = 'assets/terms_and_condtions/ABB Ability GTC-it.pdf';
    } else {
      // Default per en-EN e altre lingue
      documentPath = 'assets/terms_and_condtions/ABB Ability GTC-en.pdf';
    }

    // Crea un elemento link temporaneo per scaricare il file
    const link = document.createElement('a');
    link.href = documentPath;
    link.download = `terms-conditions-${currentLang}.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.open(documentPath, '_blank');
  }

  private setGroupedDevices(
    devices: FoundDevicesListQuery['foundDevicesList']
  ) {
    const groupedDevices = devices?.reduce(
      (acc, curr) => {
        if (curr) {
          if (!acc[curr.communicationProtocol.id]) {
            acc[curr.communicationProtocol.id] = {
              communicationProtocol: curr.communicationProtocol.name,
              devices: [],
            };
          }
          acc[curr.communicationProtocol.id].devices.push(curr);
        }
        return acc;
      },
      {} as Record<
        string,
        {
          communicationProtocol: string;
          devices: FoundDevicesListQuery['foundDevicesList'];
        }
      >
    );
    this.devices.set(groupedDevices);
  }

  scanAgain() {
    this.loadingDevices.next(true);
    timer(3000)
      .pipe(
        switchMap(() => {
          return this.apollo.client.refetchQueries({
            include: ['FoundDevicesList'],
          });
        })
      )
      .subscribe({
        next: (result) => {
          this.foundDevicesList.set(result?.[0]?.data?.foundDevicesList);
          this.setGroupedDevices(result?.[0]?.data?.foundDevicesList);
          this.loadingDevices.next(false);
        },
        error: (error) => {
          console.error('Error fetching devices:', error);
          this.loadingDevices.next(false);
        },
      });
  }

  private getDevices() {
    this.subscriptions.push(
      this.apollo
        .watchQuery<FoundDevicesListQuery, FoundDevicesListQueryVariables>({
          query: foundDevicesListQuery,
          variables: {
            propertyId: this.propertySubject.value!.id,
            serialNumberSCU200: this.gatewayToken()?.serialNumberSCU200,
          },
        })
        .valueChanges.subscribe({
          next: ({ data }) => {
            this.foundDevicesList.set(data.foundDevicesList);
            this.setGroupedDevices(data.foundDevicesList);
            this.loadingDevices.next(false);
          },
          error: (err) => {
            console.error(err);
            this.loadingDevices.next(false);
          },
        })
    );
  }

  private saveStep(opts: {
    step: number;
    stepDetails?: StepDetailsInput;
    callback?: () => Promise<void>;
  }) {
    this.nextStepBtnLoading.set(true);
    this.subscriptions.push(
      this.apollo
        .mutate<
          SaveFirstAccessWizardStepperStatusMutation,
          SaveFirstAccessWizardStepperStatusMutationVariables
        >({
          mutation: saveStepperStatusMutation,
          variables: {
            step: opts.step,
            stepDetails: opts?.stepDetails,
          },
          refetchQueries: opts.step === 2 ? [{ query: propertyListQuery }] : [],
        })
        .subscribe({
          next: ({ data }) => {
            this.cognitoService.setUserFirstAccessStep(
              data!.saveFirstAccessWizardStepperStatus
            );
            if (opts.callback) {
              opts.callback().finally(() => {
                this.nextStepBtnLoading.set(false);
                this.configureAnotherGatewayBtnLoading.set(false);
                this.configureAnotherPropertyBtnLoading.set(false);
                this.goToDashboardBtnLoading.set(false);
              });
            } else {
              this.nextStepBtnLoading.set(false);
              this.configureAnotherGatewayBtnLoading.set(false);
              this.configureAnotherPropertyBtnLoading.set(false);
              this.goToDashboardBtnLoading.set(false);
            }
          },
          error: (err) => {
            //let errMsg = getErrorMessage(err, this.translate);
            //this.toast.notifyError(errMsg);
          },
        })
    );
  }

  onBeforeGoNextStep = (): Promise<boolean> => {
    return new Promise(async (resolve, reject) => {
      const currentStepIdx = this.currentStep();
      switch (currentStepIdx) {
        case 0:
          this.propertyFormRef()?.submit();
          if (!this.propertyFormRef()?.form.valid) {
            resolve(false);
          } else {
            await firstValueFrom(
              this.propertySubject.pipe(filter((value) => value !== null))
            );
            this.saveStep({
              step: currentStepIdx + 1,
              stepDetails: {
                propertyId: this.propertySubject.value!.id,
              },
              callback: () => {
                return new Promise((res, rej) => res(resolve(true)));
              },
            });
          }
          break;
        case 1:
          this.saveStep({
            step: currentStepIdx + 1,
            stepDetails: {
              propertyId: this.propertySubject.value!.id,
              gatewayId: this.gateway()?.id,
            },
            callback: () => {
              return new Promise((res, rej) => res(resolve(true)));
            },
          });
          break;
        case 2:
          this.saveStep({
            step: currentStepIdx + 1,
            stepDetails: {
              propertyId: this.propertySubject.value!.id,
              gatewayId: this.gateway()?.id,
              devicesIds: this.foundDevicesList()?.map((d) => d!.id) ?? [],
            },
            callback: () => {
              return new Promise((res, rej) => res(resolve(true)));
            },
          });
          break;
        case 3:
          this.saveStep({
            step: currentStepIdx + 1,
            callback: () => {
              return new Promise((res, rej) => res(resolve(true)));
            },
          });
          break;
        default:
          this.saveStep({
            step: currentStepIdx + 1,
            callback: () => {
              return new Promise((res, rej) => res(resolve(true)));
            },
          });
          break;
      }
    });
  };

  onChangeStep(current: number) {
    this.currentStep.set(current);

    // Pulisci il polling dei devices quando si cambia step
    if (this.devicesPollingIntervalId) {
      clearInterval(this.devicesPollingIntervalId);
      this.devicesPollingIntervalId = null;
    }

    switch (current) {
      case 1:
        if (!this.stepDetails()?.gateway) {
          this.notLinked.set(true);
          this.checkWizardAndProceedAsync();
        }
        break;
      case 2:
        this.loadingDevices.next(true);
        // Avvia il polling solo per lo step 2
        this.devicesPollingIntervalId = setInterval(() => {
          this.getDevices();
        }, 30000);
        // if (!this.stepDetails()?.devices) {
        //   this.getDevices();
        // }
        break;
      case 3:
      case 4:
      default:
        break;
    }
  }

  async checkWizardAndProceedAsync() {
    if (!this.stepDetails()?.gateway) {
      this.notLinked.set(true);
      try {
        const result = await this.apollo
          .query<{
            checkSaveFirstAccessWizard: CheckSaveFirstAccessWizardResponse;
          }>({
            query: checkSaveFirstAccessWizard,
          })
          .toPromise();

        if (result?.data?.checkSaveFirstAccessWizard) {
          const wizardData = result.data.checkSaveFirstAccessWizard;

          this.isFirstUser.set(wizardData.isFirstUser);

          if (wizardData.proceedToGateway) {
            // Se proceedToGateway è true, richiama getGatewayToken
            this.getGatewayToken();
          } else {
            // Se proceedToGateway è false ma isFromProperty è true, mostra l'alert
            this.overlay
              .openAlert({
                message: t(
                  this.prefix,
                  `The setup wizard is currently in use. Please wait a moment and try again.`,
                  this.translate
                ),
                title: t('commons', `Warning`, this.translate),
                showConfirmButton: false,
              })
              .catch(() => {
                if (wizardData.isFromProperty) {
                  this.router.navigate(['/properties']);
                } else {
                  close();
                }
              });
          }
        }
      } catch (err) {
        console.error('Error checking first access wizard:', err);
        // Gestisci l'errore come preferisci
      }
    }
  }

  getGatewayToken() {
    this.subscriptions.push(
      this.apollo
        .query<
          GatewayConnectionTokenQuery,
          GatewayConnectionTokenQueryVariables
        >({
          query: gatewayConnectionTokenQuery,
          variables: {
            propertyId: this.propertySubject.value!.id,
          },
        })
        .subscribe({
          next: ({ data }) => {
            this.gatewayToken.set(data.gatewayConnectionToken);
            const notLinked = data.gatewayConnectionToken.status != Status.Ok;
            if (notLinked) {
              this.startCheckingToken();
            } else {
              // TODO: ottimizzare
              this.apollo
                .query<
                  GatewayConnectionCheckTokenQuery,
                  GatewayConnectionCheckTokenQueryVariables
                >({
                  query: gatewayConnectionCheckTokenQuery,
                  variables: {
                    propertyId: this.propertySubject.value!.id,
                    serialNumberSCU200: this.gatewayToken()?.serialNumberSCU200,
                  },
                })
                .pipe(first())
                .subscribe({
                  next: ({ data }) => {
                    this.gateway.set(data.gatewayConnectionCheckToken);

                    // Carica sempre la versione firmware, indipendentemente da updateAvailable
                    this.loadCurrentFirmwareVersion();

                    // Se c'è un aggiornamento disponibile, avvia il polling
                    if (
                      data.gatewayConnectionCheckToken.firmware?.updateAvailable
                    ) {
                      this.startFirmwarePolling();
                    }
                    // this.fwUpdateProcessed.set(
                    //   this.fwUpdateAvailable() &&
                    //     !data.gatewayConnectionCheckToken.firmware
                    //       ?.updateAvailable
                    // );
                    // this.fwUpdated.set(
                    //   this.fwUpdateAvailable() &&
                    //     !data.gatewayConnectionCheckToken.firmware
                    //       ?.updateAvailable
                    // );

                    this.checkingGatewayConnession.set(false);
                    this.notLinked.set(false);
                  },
                  error: (err) => {
                    this.notLinked.set(true);
                    this.waitingForWebServerResponse.set(false);
                    console.error(err);
                  },
                });
            }
          },
          error: (err) => {
            //let errMsg = getErrorMessage(err, this.translate);
            //this.toast.notifyError(errMsg);
          },
        })
    );
  }

  private checkTokenSubscription: Subscription | undefined;
  private startCheckingToken() {
    this.waitingForWebServerResponse.set(true);
    this.linkAttempts.update((prev) => [
      ...prev.map((p) => ({ ...p, loading: false })),
      {
        loading: true,
        message: t(
          this.prefix,
          `Waiting for webserver response`,
          this.translate
        ),
      },
    ]);
    this.checkTokenSubscription = timer(30000) // Initial delay of 30 seconds
      .pipe(
        switchMap(() => {
          return this.apollo
            .query<
              GatewayConnectionCheckTokenQuery,
              GatewayConnectionCheckTokenQueryVariables
            >({
              query: gatewayConnectionCheckTokenQuery,
              variables: {
                propertyId: this.propertySubject.value!.id,
                serialNumberSCU200: this.gatewayToken()?.serialNumberSCU200,
              },
            })
            .pipe(
              map(({ data }) => data.gatewayConnectionCheckToken),
              retry({
                count: 3, // Retry up to 3 times
                delay: (error, retryCount) => {
                  this.linkAttempts.update((prev) => [
                    ...prev.map((p) => ({ ...p, loading: false })),
                    {
                      loading: true,
                      message: t(
                        this.prefix,
                        `Retry attempt {retryCount}`,
                        this.translate,
                        { retryCount }
                      ),
                    },
                  ]);
                  return of(error).pipe(delay(30000)); // Delay of 30 seconds between retries
                },
              }),
              catchError(async (err) => {
                this.overlay
                  .openAlert({
                    message: t(
                      this.prefix,
                      `No response from the web server. Please check your internet connection or try again in a few minutes`,
                      this.translate
                    ),
                    title: t('commons', `Warning`, this.translate),
                    confirmText: t(this.prefix, `Retry`, this.translate),
                    showCancelButton: !this.isFirstUser(),
                  })
                  .then(() => {
                    this.linkAttempts.set([]);
                    this.apollo.client.cache.evict({
                      fieldName: 'gatewayConnectionToken',
                    });
                    this.getGatewayToken();
                  })
                  .catch(() => {
                    if (!this.isFirstUser()) {
                      this.router.navigate(['/properties']);
                    }
                    // else {
                    //   this.configureAnotherProperty(); // TODO: da capire se effettivamente in questo caso devo tornare al passaggio iniziale di configurazione nuova proprietà oppure saltare il passaggio e andare direttamente all'ultimo step di sommario (dando la possibilità di provare a riconnettersi in un altro momeno, dall'interno dell'applicativo)
                    // }
                  });
                throw err;
              })
            );
        })
      )
      .subscribe({
        next: (
          data: GatewayConnectionCheckTokenQuery['gatewayConnectionCheckToken']
        ) => {
          if (data) {
            this.gateway.set(data);

            // Carica sempre la versione firmware, indipendentemente da updateAvailable
            this.loadCurrentFirmwareVersion();

            // Se c'è un aggiornamento disponibile, avvia il polling
            if (data.firmware?.updateAvailable) {
              this.startFirmwarePolling();
            }

            this.checkingGatewayConnession.set(false);
            this.notLinked.set(false);
            this.waitingForWebServerResponse.set(false);
            this.checkTokenSubscription?.unsubscribe();
          }
        },
        error: (err) => {
          this.notLinked.set(true);
          this.waitingForWebServerResponse.set(false);
          this.checkTokenSubscription?.unsubscribe();
          console.error(err);
        },
      });
  }

  goNext(): void {
    this.nextStepBtnLoading.set(true);
    this.showDisclaimer.set(false);
    this.saveStep({
      step: 0,
      stepDetails: {
        termsAndConditions: true,
      },
    });
  }

  goBack = (): void => {
    //this.stepperRef()?.goBack();
    this.propertyFormRef()?.showContacts.set(false);
  };

  goBackBtn() {
    this.wizardCard.currentStepIndex.update((prev) => prev - 1);
  }

  handleSubmitStatusBtn(disabled: boolean): void {
    this.nextStepBtnDisabled.set(disabled);
  }

  onSubmit(form: FormGroup): void {
    if (form.invalid) {
      return;
    }
    const formValue = form.getRawValue();
    this.nextStepBtnLoading.set(true);
    this.apollo
      .mutate<CreatePropertyMutation, CreatePropertyMutationVariables>({
        mutation: createPropertyMutation,
        variables: {
          input: {
            name: formValue.propertyInfo.name,
            address: formValue.propertyInfo.address,
            fullAddress: formValue.propertyInfo.fullAddress,
            country: formValue.propertyInfo.country,
            city: formValue.propertyInfo.city,
            postalCode: formValue.propertyInfo.postalCode,
            timezone: formValue.propertyInfo.timezone,
            currency: formValue.propertyInfo.currency,
            imgUrl: formValue.propertyInfo.image,
            coordinates: formValue.propertyInfo.coordinates,
            contactsIds: formValue.contactsForm.contacts,
          },
        },
      })
      .subscribe({
        next: ({ data }) => {
          this.propertySubject.next(data?.createProperty ?? null);
          this.nextStepBtnLoading.set(false);
          this.toast.notifySuccess(
            t(this.prefix, `{name} was created successfully`, this.translate, {
              name: formValue.propertyInfo.name,
            })
          );
          this.apollo.client.refetchQueries({
            include: ['PropertyList'],
          });
        },
        error: () => {
          this.nextStepBtnLoading.set(false);
          this.toast.notifyError(
            t(
              this.prefix,
              `An error occurred while creating {name}`,
              this.translate,
              { name: formValue.propertyInfo.name }
            )
          );
        },
      });
  }

  private resetSignals(): void {
    this.notLinked.set(true);
    this.checkingGatewayConnession.set(false);
    this.fwUpdateProcessed.set(true);
    this.updatingFw.set(false);
    this.fwUpdated.set(true);
    this.linkAttempts.set([]);

    if (this.firmwareVersionPollingId) {
      clearInterval(this.firmwareVersionPollingId);
      this.firmwareVersionPollingId = null;
    }
  }

  configureAnotherProperty(): void {
    this.configureAnotherPropertyBtnLoading.set(true);
    this.propertyFormEditing.set(false);
    this.resetSignals();
    this.saveStep({
      step: 0,
      stepDetails: {
        termsAndConditions: true,
        propertyId: null,
        gatewayId: null,
        devicesIds: null,
      },
      callback: () => {
        return new Promise((res, rej) => res(this.stepperRef()?.goToStep(0)));
      },
    });
  }

  configureAnotherGateway(): void {
    this.configureAnotherGatewayBtnLoading.set(true);
    this.apollo.client.cache.evict({ fieldName: 'gatewayConnectionToken' });
    this.apollo.client.cache.evict({
      fieldName: 'gatewayConnectionCheckToken',
    });
    this.resetSignals();
    this.saveStep({
      step: 1,
      stepDetails: {
        termsAndConditions: true,
        propertyId: this.propertySubject.value!.id,
        gatewayId: null,
        devicesIds: null,
      },
      callback: () => {
        return new Promise((res, rej) => res(this.stepperRef()?.goToStep(1)));
      },
    });
  }

  goToDashboard(): void {
    this.goToDashboardBtnLoading.set(true);
    this.saveStep({
      step: FIRST_ACCESS_WIZARD_LAST_STEP,
      callback: () => {
        return new Promise((res, rej) => {
          this.router
            .navigate([`${URLS.BASE}${URLS.DASHBOARDS}`])
            .finally(() => res());
        });
      },
    });
  }

  private loadCurrentFirmwareVersion(): void {
    const gatewayId = this.gateway()?.id;
    if (!gatewayId) return;

    if (this.firmwareVersionPollingId) {
      clearInterval(this.firmwareVersionPollingId);
      this.firmwareVersionPollingId = null;
    }

    this.startFirmwareVersionPolling(gatewayId);
  }

  private startFirmwareVersionPolling(gatewayId: string): void {
    const pollFirmwareVersion = () => {
      this.apollo
        .query<FirmwareVersionQuery, FirmwareVersionQueryVariables>({
          query: firmwareVersion,
          variables: { gatewayId },
          fetchPolicy: 'network-only',
        })
        .subscribe({
          next: ({ data }) => {
            if (data.firmwareVersion) {
              if (
                data.firmwareVersion.firmwareVersion &&
                data.firmwareVersion.firmwareVersion.trim() !== ''
              ) {
                this.firmwareVersionData.set(data.firmwareVersion);
                if (this.firmwareVersionPollingId) {
                  clearInterval(this.firmwareVersionPollingId);
                  this.firmwareVersionPollingId = null;
                }
              }
            }
          },
          error: (err) => {},
        });
    };

    pollFirmwareVersion();

    this.firmwareVersionPollingId = setInterval(pollFirmwareVersion, 3000);
  }

  private startFirmwarePolling(): void {
    const gatewayId = this.gateway()?.id;
    if (!gatewayId) return;

    this.firmwareIntervalId = setInterval(() => {
      this.subscriptions.push(
        this.apollo
          .query<FirmwareVersionQuery, FirmwareVersionQueryVariables>({
            query: firmwareVersion,
            variables: { gatewayId },
          })
          .subscribe({
            next: ({ data }) => {
              if (data.firmwareVersion) {
                this.firmwareVersionData.set(data.firmwareVersion);

                if (data.firmwareVersion.IsLastFirmwareVersion) {
                  this.fwUpdateProcessed.set(true);
                  this.updatingFw.set(false);

                  if (this.firmwareIntervalId) {
                    clearInterval(this.firmwareIntervalId);
                    this.firmwareIntervalId = null;
                  }
                } else {
                }
              }
            },
            error: (err) => {
              console.error('Errore nel polling firmware:', err);
            },
          })
      );
    }, 5000);
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((s) => s.unsubscribe());
    if (this.checkTokenSubscription) {
      this.checkTokenSubscription.unsubscribe();
    }
    if (this.firmwareIntervalId) {
      clearInterval(this.firmwareIntervalId);
      this.firmwareIntervalId = null;
    }
    if (this.devicesPollingIntervalId) {
      clearInterval(this.devicesPollingIntervalId);
      this.devicesPollingIntervalId = null;
    }
    if (this.firmwareVersionPollingId) {
      clearInterval(this.firmwareVersionPollingId);
      this.firmwareVersionPollingId = null;
    }
  }
}
